import { useMemo, useState } from "react";
import {
  usePipelines,
  type PipelineObjectType,
} from "../../hooks/usePipelines";
import panel from "./AddWorkflowPanel.module.css";
import styles from "./AddPipelinePanel.module.css";

interface Props {
  portalId: string;
  onAdd: (objectType: PipelineObjectType, pipelineId: string) => void;
  placedKeys: Set<string>;
  onClose: () => void;
}

const TABS: { key: PipelineObjectType; label: string }[] = [
  { key: "deals", label: "Deals" },
  { key: "tickets", label: "Tickets" },
];

export default function AddPipelinePanel({
  portalId,
  onAdd,
  placedKeys,
  onClose,
}: Props) {
  const [objectType, setObjectType] = useState<PipelineObjectType>("deals");
  const { items, loading, error } = usePipelines(portalId, objectType);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) => p.label.toLowerCase().includes(q));
  }, [items, query]);

  return (
    <div className={panel.panel}>
      <header className={panel.header}>
        <h2 className={panel.title}>Add pipeline</h2>
        <button
          type="button"
          className={panel.close}
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
      </header>

      <div className={styles.tabs}>
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`${styles.tab} ${objectType === t.key ? styles.tabActive : ""}`}
            onClick={() => setObjectType(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <input
        className={panel.search}
        placeholder="Search pipelines…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      {loading && <div className={panel.empty}>Loading…</div>}
      {error && <div className={panel.error}>{error}</div>}
      {!loading && !error && filtered.length === 0 && (
        <div className={panel.empty}>No pipelines match.</div>
      )}

      <ul className={panel.list}>
        {filtered.map((p) => {
          const placed = placedKeys.has(`${objectType}:${p.id}`);
          return (
            <li key={p.id}>
              <button
                type="button"
                className={panel.row}
                disabled={placed}
                onClick={() => onAdd(objectType, p.id)}
              >
                <span className={panel.rowName}>{p.label}</span>
                <span className={panel.rowMeta}>
                  {p.stages.length} stage{p.stages.length === 1 ? "" : "s"}
                  {placed ? " · placed" : ""}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
